import { useQuery } from '@tanstack/react-query'
import {
  Accordion,
  AccordionItem,
  Avatar,
  Badge,
  Button,
  DateOrTime,
  DevDetailsButton,
  Drawer,
  Flex,
  InputContainer,
  Menu,
  MenuOption,
  PaperClipIcon,
  PlusIcon,
  ReadOnlyProperty,
  ReadOnlyPropertyProps,
  Text,
  TextField,
} from 'components'
import { SparklesIcon, UsersIcon } from 'components/icons'
import { useAlert, useWorkspace } from 'components/providers'
import { DataType } from 'ka-table'
import { isEmpty, startCase } from 'lodash-es'
import { ReactNode, isValidElement, useEffect, useState } from 'react'
import {
  GeoLeadProperties,
  HelperLeadProperties,
  IdentityLeadProperty,
  Lead,
  LeadTemperature,
  OmitDBProps,
  PrimaryLeadProperty,
  SessionWithLead,
  WorkspaceProperty,
  leadProperties,
  leadTemperatures,
} from 'types'
import { sleep, useDisclosure, usePermission, useUsers } from 'utils'
import { makeTypeByField } from 'utils/tableUtils'
import { SessionMediaViewer } from '.'
import {
  createTraitMap,
  formatPhoneNumbers,
  getActionMetrics,
  getMomentTitle,
  getSessionDuration,
  getSessionMedia,
  getSessionSource,
  hideColumns,
} from '../../utils'
import { useEvent } from '../../utils/eventUtils'
import LeadTempIcon from './LeadTempIcon'

interface Props {
  session?: SessionWithLead
  onDone: (body?: Lead & { id: string }) => void
}

export default function SessionDrawer({ session, onDone }: Props) {
  const { event } = useEvent()
  const { workspace } = useWorkspace()
  const { confirm } = useAlert()

  const { open, onOpen, onClose } = useDisclosure()
  const [draft, setDraft] = useState<Lead>()

  useEffect(() => {
    if (!session) return
    const { lead } = session
    if (lead) {
      const { visibleEditProperties } = parseEditProperties(lead)
      const draft: OmitDBProps<Lead> = {}
      for (const [key, value] of Object.entries(visibleEditProperties)) {
        if (value == null) continue
        const primaryKey = key as EditProperty
        if (editPrimaryProperties.includes(primaryKey)) draft[primaryKey] = value
        else draft.custom = { ...draft.custom, [key]: value }
      }
      setDraft(draft)
    }
    onOpen()
  }, [session])

  if (!session) return null

  const workspaceId = workspace?.id ?? ''

  const { appName, appTitle, created, traits, metrics, momentId, lead, mediaStatus, ...rest } =
    session
  const { apiDataStatus, badge, crmSync, name, followUp, optIn } = lead || {}
  const duration = getSessionDuration(metrics)
  const source = getSessionSource(appName, appTitle)
  const traitMap = createTraitMap(traits)
  const actionMetrics = getActionMetrics(metrics)
  const combined = {
    ...rest,
    apiDataStatus,
    badge,
    crmSync,
    followUp,
    optIn,
    duration,
    source,
    mediaStatus,
  }
  const remainingFieldMap = hideColumns(combined, 'panel')
  const fieldTypes = makeTypeByField([remainingFieldMap])
  const hasEdits = Object.entries(draft || {}).some(([key, value]) => {
    if (key === 'custom') {
      return Object.entries(value).some(([k, v]) => lead?.custom?.[k] !== v)
    }
    return (lead?.[key as PrimaryLeadProperty] || '') !== (value || '')
  })

  async function handleSave() {
    if (!lead) return
    handleDone({ id: lead.id, ...draft })
  }

  async function handleClose() {
    if (hasEdits) {
      const confirmed = await confirm('Discard changes?')
      if (!confirmed) return
    }

    handleDone()
  }

  async function handleDone(body?: Lead & { id: string }) {
    onClose()
    await sleep(200)
    setDraft(undefined)
    onDone(body)
  }

  let items: AccordionItem[] = [
    {
      title: 'Trait Selections',
      content: (
        <Flex direction="column" gap={2} px={6}>
          {Object.entries(traitMap || {})
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
            .map(([key, value]) => (
              <ReadOnlyProperty key={key} label={key} value={value} dataType={fieldTypes[key]} />
            ))}
        </Flex>
      ),
    },
    {
      title: 'Content Viewed',
      content: (
        <Flex direction="column" gap={2} px={6}>
          {actionMetrics?.length ? (
            [...actionMetrics]
              .sort((a, b) => a.title.localeCompare(b.title))
              .map(({ title, duration }) => (
                <ReadOnlyProperty
                  key={title}
                  label={title}
                  value={duration ? `${duration} seconds` : 'N/A'}
                  dataType={DataType.String}
                />
              ))
          ) : (
            <Text textStyle="bodyMedium" fontWeight="500" as="i" opacity={0.5}>
              No content viewed
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: (
        <MediaAccordionTitle eventId={event?.id} sessionId={session.id} workspaceId={workspaceId} />
      ),
      content: (
        <Flex direction="column" gap={2} px={4}>
          {mediaStatus === 'uploaded' && (
            <SessionMediaViewer
              eventId={event?.id}
              sessionId={session.id}
              workspaceId={workspaceId}
            />
          )}
        </Flex>
      ),
    },
    {
      title: 'Additional Details',
      content: (
        <Flex direction="column" gap={2} px={6}>
          {Object.entries(remainingFieldMap)
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
            .map(([key, value]) => (
              <ReadOnlyProperty
                key={key}
                label={key}
                value={formatReadOnlyValue({ key, value })}
                dataType={fieldTypes[key]}
              />
            ))}
        </Flex>
      ),
    },
  ]

  items = items.filter(({ content }) => {
    //@ts-ignore
    const { children } = content.props
    return Array.isArray(children) ? children.some(isValidElement) : isValidElement(children)
  })

  return (
    <Drawer
      header={(name ?? 'Anonymous Session') || 'Unnamed Lead'}
      headerActions={
        <DevDetailsButton
          items={[
            {
              table: session.lead ? 'leads' : 'leadSessions',
              id: session.lead?.id ?? session.id,
              label: session.lead ? 'Lead' : 'Session',
              type: 'dc',
            },
            {
              table: 'leadSessions',
              id: session.id,
              label: 'Session',
              type: 'dc',
            },
          ]}
        />
      }
      bodyProps={{
        px: 0,
      }}
      footer={
        hasEdits ? (
          <>
            <Button variant="ghost" onClick={() => handleDone()}>
              Cancel
            </Button>
            <Button palette="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        ) : undefined
      }
      onClose={handleClose}
      open={open}
    >
      <Flex direction="column" gap={2} px={5}>
        <Flex gap={1} align="center" textStyle="bodySmall">
          <Text>Captured</Text>
          <DateOrTime py={1} px={2} background="gray.200" borderRadius="sm">
            {created}
          </DateOrTime>
          <Text>from</Text>
          <Text py={1} px={2} background="gray.200" borderRadius="sm">
            {event ? getMomentTitle(event, session) : 'On-the-Go'}
          </Text>
        </Flex>

        <LeadProperties
          lead={draft}
          onUpdate={v => setDraft(s => ({ ...s, ...v }))}
          fieldTypes={fieldTypes}
        />
      </Flex>
      <Accordion
        multiple
        items={items}
        defaultValue={
          items.length > 2
            ? actionMetrics?.length
              ? ['0', '1']
              : ['0']
            : actionMetrics?.length
              ? ['0']
              : []
        }
        buttonProps={{ backgroundColor: 'transparent', px: 6 }}
      />
    </Drawer>
  )
}

interface LeadPropertiesTypes {
  fieldTypes: Record<string, DataType>
  lead: Lead | undefined
  onUpdate: (body: Partial<Lead>) => void
}

function LeadProperties({ lead, onUpdate, fieldTypes }: LeadPropertiesTypes) {
  const permission = usePermission()
  const { workspace } = useWorkspace()
  const canEdit = permission.event === 'admin'

  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(!isEmpty(lead)), [lead])

  if (!lead) return null

  const { smartProperties, smartPropertyFailure } = lead || {}
  const { visibleEditProperties, hiddenEditProperties } = parseEditProperties(
    lead,
    workspace?.properties
  )

  return (
    <>
      {Object.keys(visibleEditProperties).map(key =>
        canEdit ? (
          <EditProperty
            autofocus={mounted}
            editor={getEditPropertyConfig(key)}
            key={key}
            label={key}
            onChange={v =>
              onUpdate(
                typeof v === 'string'
                  ? editPrimaryProperties.includes(key as EditProperty)
                    ? { [key]: v }
                    : { custom: { ...lead.custom, [key]: v } }
                  : v
              )
            }
            value={visibleEditProperties[key]}
          />
        ) : (
          <ReadOnlyProperty
            key={key}
            label={startCase(key)}
            value={formatReadOnlyValue({
              label: startCase(key),
              value: visibleEditProperties[key],
            })}
            dataType={fieldTypes[key]}
          />
        )
      )}

      {canEdit && (
        <UnusedPropertyMenu
          properties={Object.keys(hiddenEditProperties)}
          onSelect={key =>
            onUpdate(
              editPrimaryProperties.includes(key as EditProperty)
                ? { [key]: '' }
                : { custom: { ...lead.custom, [key]: '' } }
            )
          }
        />
      )}

      {!!smartProperties?.length && (
        <Flex direction="column" gap={2} my={4}>
          <Flex gap={3} align="center">
            <SparklesIcon color="primary.500" />
            <Text textStyle="bodyMedium">Smart Columns</Text>
          </Flex>
          {smartPropertyFailure && (
            <ReadOnlyProperty
              key="danger"
              value="Smart columns has an issue, please check the configuration"
              textProps={{
                color: 'danger.500',
                opacity: 0.8,
                as: 'i',
              }}
            />
          )}

          {smartProperties.map(({ name, number, text }) => (
            <ReadOnlyProperty
              key={name}
              label={startCase(name)}
              value={text || number || 'No Match Found'}
              textProps={{ ...(!text && !number && { color: 'danger.500', as: 'i' }) }}
              dataType={fieldTypes[name]}
            />
          ))}
        </Flex>
      )}
    </>
  )
}

function formatReadOnlyValue({ label, value }: ReadOnlyPropertyProps) {
  switch (label) {
    case 'duration':
      return `${value} seconds`

    case 'Follow Up Owner':
      return value.label

    case 'Temp':
      return (
        <Flex align="center" gap={2}>
          <LeadTempIcon value={value as LeadTemperature} />
          <Text textStyle="bodySmall" fontWeight="500">
            {startCase(value as LeadTemperature)}
          </Text>
        </Flex>
      )

    default:
      return value?.toString?.()
  }
}

interface EditPropertyProps extends EditorMenuProps {
  autofocus?: boolean
  editor?: EditorTypes
}

function EditProperty({ autofocus, label, value, editor, onChange }: EditPropertyProps) {
  let correctedLabel = label
  if (label === 'followUpGroup' || label === 'followUpUserId') correctedLabel = 'followUpOwner'
  const displayLabel = leadProperties.includes(label as keyof Lead)
    ? startCase(correctedLabel)
    : correctedLabel

  return (
    <InputContainer label={displayLabel}>
      {(() => {
        switch (editor) {
          case 'input':
          case 'textarea':
            return (
              <TextField
                autoFocus={autofocus}
                size="sm"
                isArea={editor === 'textarea'}
                value={label === 'phone' ? formatPhoneNumbers(value) : value || ''}
                onChange={e => onChange(e.target.value)}
              />
            )
          case 'menu': {
            return <EditorMenu label={correctedLabel} value={value} onChange={onChange} />
          }

          default:
            return <Text>{value}</Text>
        }
      })()}
    </InputContainer>
  )
}

interface EditorMenuProps {
  label: string
  value: any
  onChange: (body: string | Partial<Lead>) => void
}

function EditorMenu({ label, value, onChange }: EditorMenuProps) {
  const { workspace } = useWorkspace()
  const [menuOptions, setMenuOptions] = useState<ReactNode>(null)
  const { data: users } = useUsers(workspace?.userIds ?? [])

  useEffect(() => {
    async function fetchOptions() {
      switch (label) {
        case 'temp':
          setMenuOptions(
            <Menu
              variant="outline"
              borderRadius="10px"
              menuProps={{ positioning: { sameWidth: true } }}
              options={[...leadTemperatures, null].map(key => ({
                value: key || 'unset',
                label: (
                  <Flex align="center" gap={3}>
                    <LeadTempIcon value={key as LeadTemperature} />
                    <Text textStyle="bodySmall" fontWeight="500">
                      {startCase(key || 'unset')}
                    </Text>
                  </Flex>
                ),
              }))}
              value={value || 'unset'}
              onChange={value =>
                onChange({
                  temp: value === 'unset' ? undefined : (value as LeadTemperature),
                })
              }
            />
          )
          break

        case 'followUpOwner':
          const ownerGroups = workspace?.followUpGroups
            ? Object.values(workspace.followUpGroups).map(group => group.title)
            : []
          const ownerMembers = workspace?.followUpUsersDisabled ? [] : users
          const menuOptions: MenuOption<string>[] = [
            {
              type: 'group',
              title: 'Groups',
              options: [
                ...ownerGroups.map(value => ({
                  type: 'item' as const,
                  value,
                  label: (
                    <Flex align="center" gap={3}>
                      <UsersIcon color="rgba(113, 113, 113, 1)" />
                      <Text textStyle="bodySmall" fontWeight="500">
                        {value}
                      </Text>
                    </Flex>
                  ),
                  onClick: () => {
                    onChange({ followUpGroup: value, followUpUserId: undefined })
                  },
                })),
              ],
            },
            ...(ownerMembers?.length
              ? [
                  { type: 'divider' as const },
                  {
                    type: 'group' as const,
                    title: 'Momentify Members',
                    options: ownerMembers.map(user => ({
                      type: 'item' as const,
                      value: user?.id ?? '',
                      label: (
                        <Flex align="center" gap={2} overflow="hidden">
                          <Avatar src={user?.photoUrl} name={user?.name} size="sm" />
                          <Text textStyle="bodySmall" fontWeight="500">
                            {user?.name}
                          </Text>
                        </Flex>
                      ),
                      onClick: () => {
                        onChange({ followUpGroup: undefined, followUpUserId: user?.id ?? '' })
                      },
                    })),
                  },
                ]
              : []),
          ]
          setMenuOptions(
            !(workspace?.followUpUsersDisabled && ownerGroups.length === 0) ? (
              <Menu
                variant="outline"
                borderRadius="10px"
                menuProps={{ positioning: { sameWidth: true } }}
                value={value}
                options={menuOptions}
              />
            ) : (
              <ReadOnlyProperty key={value} value={value} dataType={DataType.String} />
            )
          )
          break
      }
    }

    fetchOptions()
  }, [label, value, workspace, users, onChange])

  return menuOptions
}

interface UnusedPropertyMenuProps {
  properties: (EditProperty | string)[]
  onSelect: (key: string) => void
}

function UnusedPropertyMenu({ properties, onSelect }: UnusedPropertyMenuProps) {
  if (!properties.length) return null

  return (
    <Menu
      buttonLabel="Empty Fields"
      size="sm"
      borderRadius="xs"
      palette="primary"
      leftIcon={<PlusIcon fontSize="small" />}
      hideCheckbox
      options={properties.map(field => ({
        value: field,
        label: startCase(field),
        onClick: () => onSelect(field),
      }))}
    />
  )
}

type EditProperty =
  | IdentityLeadProperty
  | keyof Omit<GeoLeadProperties, 'fullOriginalAddress' | 'lat' | 'lng' | 'county'>
  | keyof Pick<HelperLeadProperties, 'followUpGroup' | 'followUpUserId' | 'notes' | 'temp'>

const editPrimaryProperties: EditProperty[] = [
  'firstName',
  'lastName',
  'temp',
  'email',
  'phone',
  'followUpGroup',
  'followUpUserId',
  'notes',
  'title',
  'company',
  'school',
  'address',
  'address2',
  'city',
  'zip',
  'state',
  'country',
]

function getEditPropertyConfig(property: string): EditorTypes {
  const isNotPrimaryProperty = !editPrimaryProperties.includes(property as EditProperty)
  if (isNotPrimaryProperty) return 'input'
  else {
    switch (property) {
      case 'address':
      case 'address2':
      case 'city':
      case 'company':
      case 'country':
      case 'email':
      case 'firstName':
      case 'lastName':
      case 'phone':
      case 'school':
      case 'state':
      case 'title':
      case 'zip':
        return 'input'

      case 'followUpGroup':
      case 'followUpUserId':
      case 'temp':
        return 'menu'

      case 'notes':
        return 'textarea'

      default:
        return 'input'
    }
  }
}

function parseEditProperties(lead: OmitDBProps<Lead>, properties?: WorkspaceProperty[]) {
  const workspaceProperties = properties?.map(property => property.title) || []
  const leadPropertyKeys = [
    ...editPrimaryProperties,
    ...Object.keys(lead.custom || {}),
    ...workspaceProperties,
  ]
  const getValue = (key: EditProperty | string) =>
    editPrimaryProperties.includes(key as EditProperty)
      ? (lead as Record<string, any>)[key]
      : lead.custom?.[key]
  const isVisible = (key: EditProperty | string) => {
    if (key === 'temp') return true
    const hasValue = getValue(key) != null && getValue(key) !== ''
    const keyExistsInLead = editPrimaryProperties.includes(key as EditProperty)
      ? key in (lead as Record<string, any>)
      : key in (lead.custom || {})
    return hasValue || keyExistsInLead
  }
  const visibleEditProperties: Record<string, any> = {}
  const hiddenEditProperties: Record<string, any> = {}
  for (const key of leadPropertyKeys) {
    const value = getValue(key)
    if (isVisible(key)) visibleEditProperties[key] = value
    else hiddenEditProperties[key] = value
  }
  return { visibleEditProperties, hiddenEditProperties }
}

type EditorTypes = 'input' | 'textarea' | 'menu'
interface MediaAccordionTitleProps {
  eventId?: string
  sessionId: string
  workspaceId: string
}

function MediaAccordionTitle({ eventId, sessionId, workspaceId }: MediaAccordionTitleProps) {
  const { data } = useQuery({
    queryKey: ['sessions', workspaceId, eventId, sessionId],
    queryFn: () => getSessionMedia(workspaceId, sessionId, eventId),
  })
  const count = data?.length ?? 0

  return (
    <Flex align="center" gap={3}>
      <PaperClipIcon color="primary.500" size="sm" />
      <Text fontWeight="600" textStyle="bodyLarge">
        Attachments
      </Text>
      <Badge colorPalette="primary" size="sm" variant="subtle">
        {count}
      </Badge>
    </Flex>
  )
}
